import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../ui/dialog'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import { TierBadge } from './TierBadge'
import { ArticleReaderPane } from './ArticleReaderPane'
import { useStorySources } from '../../hooks/stories'
import { useConfirmedExternalLink } from './OffSiteLinkConfirmModal'

function formatPublishedDate(iso: string): string {
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return iso
  return d.toISOString().slice(0, 10)
}

/**
 * Modal that lists every source clustered into a story.
 *
 * Each row exposes a per-article reader toggle with three states driven by
 * `fullTextFetchedAt` + `fullText`:
 *   - `fullTextFetchedAt` null → toggle hidden (pre-feature article)
 *   - `fullTextFetchedAt` set + `fullText` non-null → reader pane shows body
 *   - `fullTextFetchedAt` set + `fullText` null → reader pane shows
 *     "Content not retrievable" with a "continue to source" link
 */
export function SourcesModal({
  storyId,
  open,
  onClose,
}: {
  storyId: string
  open: boolean
  onClose: () => void
}) {
  const { data, isLoading, error } = useStorySources(open ? storyId : '')
  const { confirmAndOpen, modal } = useConfirmedExternalLink()
  const [readerArticleId, setReaderArticleId] = useState<string | null>(null)

  return (
    <>
      <Dialog
        open={open}
        onOpenChange={(o) => {
          if (!o) {
            setReaderArticleId(null)
            onClose()
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
          <DialogHeader>
            <DialogTitle>Sources</DialogTitle>
            <DialogDescription>
              All articles clustered into this story. Click any source to view the original.
            </DialogDescription>
          </DialogHeader>

          {isLoading && (
            <p className="py-6 text-center text-sm text-muted-foreground">
              Loading sources…
            </p>
          )}
          {error && (
            <p className="py-6 text-center text-sm text-rose-600">
              Failed to load sources.
            </p>
          )}

          {data && (
            <div className="overflow-y-auto flex-1">
              <ul className="divide-y">
                {data.sources.map((s) => {
                  const isReaderOpen = readerArticleId === s.articleId
                  // KAN-48 — status-aware CTA. See StoryDetailSourcesSection
                  // for the parity rationale.
                  const status = s.fullTextStatus ?? null
                  const readerAvailable =
                    status != null ? true : s.fullTextFetchedAt !== null
                  const closedLabel =
                    status != null && status !== 'available'
                      ? 'Full text unavailable'
                      : 'Read article'

                  return (
                    <li key={s.articleId} className="py-3">
                      <div className="flex items-center gap-3">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-foreground">
                            {s.publisher}
                          </span>
                          <TierBadge tier={s.sourceTier} />
                        </div>
                        <button
                          type="button"
                          className="flex-1 truncate text-left text-sm text-primary underline-offset-2 hover:underline"
                          onClick={() => confirmAndOpen(s.url)}
                        >
                          {s.title}
                        </button>
                        <span className="text-xs text-muted-foreground tabular-nums">
                          {formatPublishedDate(s.publishedAt)}
                        </span>
                        {s.isCanonical && <Badge variant="secondary">Canonical</Badge>}
                        {readerAvailable && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="shrink-0 text-xs"
                            onClick={() =>
                              setReaderArticleId(isReaderOpen ? null : s.articleId)
                            }
                          >
                            {isReaderOpen ? 'Close' : closedLabel}
                          </Button>
                        )}
                      </div>

                      {isReaderOpen && (
                        <div className="mt-3">
                          <ArticleReaderPane
                            fullText={s.fullText}
                            fullTextMeta={s.fullTextMeta ?? null}
                            fullTextStatus={s.fullTextStatus ?? null}
                            fullTextReason={s.fullTextReason ?? null}
                            publisher={s.publisher}
                            url={s.url}
                            onContinueToSource={confirmAndOpen}
                          />
                        </div>
                      )}
                    </li>
                  )
                })}
              </ul>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {modal}
    </>
  )
}
